import { useEffect, useRef } from 'react';
import useCreateReportFromChat from './useCreateReportFromChat';

// The assistant's frontend-executed report tool. The chat request opts in by
// sending CREATE_REPORT_CLIENT_TOOLS as `clientTools`; the backend only offers
// the tool to the model for opted-in requests, so a chat that mounts this hook
// is exactly a chat whose model can call it.
export const CREATE_REPORT_TOOL = 'create_report';
export const CREATE_REPORT_CLIENT_TOOLS = [CREATE_REPORT_TOOL];
const CREATE_REPORT_PART = `tool-${CREATE_REPORT_TOOL}`;

// Tool results are written for the model, not the user: they must tell it how
// to recover (or to stop), since the model narrates the outcome to the user.
const NO_WIDGETS_ERROR =
    'No renderable data widgets exist in this chat yet. Fetch the data with ' +
    'get_entity_data first, then call create_report again. If that also ' +
    'fails, tell the user you could not build the report — do not keep ' +
    'retrying.';
const CREATE_FAILED_ERROR =
    'Report creation failed unexpectedly. Tell the user the report could ' +
    'not be saved and suggest trying again later — do not retry now.';
const DUPLICATE_CALL_ERROR =
    'Duplicate create_report call: only the first call in a turn is ' +
    'executed. Do not call create_report more than once.';

const firstUserMessageText = (messages) => {
    const first = messages.find((m) => m.role === 'user');
    return (first?.parts ?? [])
        .filter((p) => p.type === 'text')
        .map((p) => p.text ?? '')
        .join(' ')
        .trim();
};

/**
 * Execute the assistant's create_report tool calls for a chat.
 *
 * Runs on the stream-settle transition (streaming/submitted -> ready), NOT in
 * useChat's onToolCall: settle guarantees the messages array already holds the
 * whole turn's get_entity_data parts (the exact ones harvested into widgets),
 * and restored sessions never pass through a streaming status, so a persisted
 * pending tool part is never re-executed on reopen.
 *
 * A settle that follows an abort (`wasAborted`) executes nothing: the user
 * cancelled the turn — or a chat switch stopped the stream while the messages
 * still hold the abandoned conversation — so creating the report (and
 * auto-resubmitting into that chat) would be wrong. The parts stay pending;
 * if the user resumes the chat, the backend's missing-response placeholder
 * answers them and the model recovers.
 *
 * Otherwise every pending call is answered via addToolOutput — the first with
 * the create's outcome (success payload, a no-widgets error, or a
 * create-failure error), any same-turn extras with a duplicate-call error —
 * so the SDK's sendAutomaticallyWhen can resubmit and the model always gets
 * to respond. An unanswered call would stall the resubmission entirely.
 *
 * @param {object} args
 * @param {Array} args.messages - live useChat messages
 * @param {string} args.status - useChat status
 * @param {Function} args.addToolOutput - useChat's addToolOutput
 * @param {Function} [args.wasAborted] - () => boolean; true when the settle
 *   being handled came from a user/programmatic stop, not a natural end
 * @param {string} args.source - report-created analytics source
 * @param {Function} [args.onCreated] - (report, output) after a successful create
 * @param {Function} [args.onFailed] - (error) after a create-failure (not no-widgets)
 */
export default function useCreateReportTool({
    messages,
    status,
    addToolOutput,
    wasAborted,
    source,
    onCreated,
    onFailed,
}) {
    const { createFromChat, isSaving } = useCreateReportFromChat();

    // The only state the hook owns is protocol state: which calls started
    // executing (exactly-once across settles) and whether the previous effect
    // run saw an active stream. The effect re-runs on every dep change, but a
    // run is a no-op unless it observes the active -> settled TRANSITION, so
    // re-runs from message/callback churn cost nothing.
    const startedRef = useRef(new Set());
    const wasActiveRef = useRef(false);

    useEffect(() => {
        // Settled = no longer streaming/submitted, whatever the SDK's resting
        // status string is ('ready' or 'idle').
        const isActive = status === 'streaming' || status === 'submitted';
        const wasActive = wasActiveRef.current;
        wasActiveRef.current = isActive;
        if (!wasActive || isActive || status === 'error') return;
        if (wasAborted?.()) return;

        const lastAssistant = [...messages]
            .reverse()
            .find((m) => m.role === 'assistant');
        // Require a toolCallId (mirrors the server's _separate_tool_calls
        // guard): an id-less part could never have its output attached, so
        // executing it would orphan the report and stall the resubmission.
        const pending = (lastAssistant?.parts ?? []).filter(
            (p) =>
                p.type === CREATE_REPORT_PART &&
                p.state === 'input-available' &&
                p.toolCallId &&
                !startedRef.current.has(p.toolCallId),
        );
        if (!pending.length) return;
        pending.forEach((p) => startedRef.current.add(p.toolCallId));

        const respond = (toolCallId, output) =>
            addToolOutput({ tool: CREATE_REPORT_TOOL, toolCallId, output });

        // Answer same-turn extras immediately — an unanswered call would keep
        // sendAutomaticallyWhen from ever resubmitting.
        const [part, ...extras] = pending;
        extras.forEach(({ toolCallId }) =>
            respond(toolCallId, {
                success: false,
                error: DUPLICATE_CALL_ERROR,
            }),
        );

        const { toolCallId } = part;
        const input = part.input ?? {};

        (async () => {
            try {
                const prompt =
                    (input.goal ?? '').trim() || firstUserMessageText(messages);
                const report = await createFromChat({
                    prompt,
                    messages,
                    title: input.title ?? '',
                    source,
                });
                if (!report) {
                    respond(toolCallId, {
                        success: false,
                        error: NO_WIDGETS_ERROR,
                    });
                    return;
                }
                const output = {
                    success: true,
                    report_id: report.id,
                    title: report.config?.title || prompt,
                    widget_count: report.config?.widgets?.length ?? 0,
                    url: `/reports/${report.id}/view`,
                };
                respond(toolCallId, output);
                onCreated?.(report, output);
            } catch (err) {
                respond(toolCallId, {
                    success: false,
                    error: CREATE_FAILED_ERROR,
                });
                onFailed?.(err);
            }
        })();
    }, [
        messages,
        status,
        addToolOutput,
        wasAborted,
        source,
        createFromChat,
        onCreated,
        onFailed,
    ]);

    return { isCreating: isSaving };
}
